import React, { useEffect, useState } from "react";
import AuthService from "services/auth.service";
import { API_URL_IMAGE } from "../../services/config";
import { FaDownload, FaEye, FaSort, FaSortUp, FaSortDown, FaMobile, FaDesktop } from "react-icons/fa";

const GiftCardAdminView = () => {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchGiftCards();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchGiftCards = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await AuthService.getAllGiftCards();
      setGiftCards(response);
    } catch (error) {
      console.error("Error fetching gift cards:", error);
      setError("Failed to fetch gift cards. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const sortedGiftCards = [...giftCards].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === "createdAt") {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const downloadImage = (imageUrl, filename) => {
    const link = document.createElement("a");
    link.href = `${API_URL_IMAGE}${imageUrl}`;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ color: '#9CA3AF', marginLeft: 4 }} />;
    return sortDirection === "asc" ? 
      <FaSortUp style={{ color: '#2563EB', marginLeft: 4 }} /> : 
      <FaSortDown style={{ color: '#2563EB', marginLeft: 4 }} />;
  };

  // Container styles
  const containerStyle = {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '16px 8px',
    marginTop: '100px'
  };

  // Header styles
  const headerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    marginBottom: 16,
    gap: 8
  };

  const titleStyle = {
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 'bold',
    color: '#111827',
    margin: 0
  };

  const badgeStyle = {
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: 4
  };

  const buttonStyle = {
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '14px'
  };

  // Error styles
  const errorStyle = {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#DC2626',
    padding: '12px 16px',
    borderRadius: 6,
    margin: '16px'
  };

  // Modal styles
  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16
  };

  const modalContentStyle = {
    backgroundColor: 'white',
    borderRadius: 8,
    maxWidth: 600,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  };

  // Table styles
  const tableContainerStyle = {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    backgroundColor: '#F9FAFB',
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    borderBottom: '1px solid #E5E7EB',
    cursor: 'pointer',
    userSelect: 'none'
  };

  const tdStyle = {
    padding: '16px',
    borderBottom: '1px solid #E5E7EB',
    fontSize: '14px'
  };

  // Mobile card styles
  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E5E7EB',
    padding: 12,
    marginBottom: 12
  };

  // Action button styles
  const actionButtonStyle = {
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
    border: 'none',
    padding: '6px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    margin: '2px'
  };

  const downloadButtonStyle = {
    backgroundColor: '#DCFCE7',
    color: '#166534',
    border: 'none',
    padding: '6px 12px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    margin: '2px'
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 256 }}>
      <div style={{ fontSize: '18px' }}>Loading gift cards...</div>
    </div>
  );

  if (error) return (
    <div style={errorStyle}>
      {error}
      <button 
        onClick={fetchGiftCards}
        style={{ ...buttonStyle, backgroundColor: '#DC2626', marginLeft: 16 }}
      >
        Retry
      </button>
    </div>
  );

  return (
    <div style={containerStyle}>
      {/* Image Modal */}
      {selectedImage && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottom: '1px solid #E5E7EB' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Image Preview</h3>
              <button 
                onClick={() => setSelectedImage(null)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B7280' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 16 }}>
              <img 
                src={`${API_URL_IMAGE}${selectedImage}`} 
                alt="Preview" 
                style={{ maxWidth: '100%', maxHeight: 320, margin: '0 auto', display: 'block' }}
              />
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => downloadImage(selectedImage, `giftcard-${Date.now()}.jpg`)}
                  style={{ ...buttonStyle, display: 'flex', alignItems: 'center', fontSize: '14px' }}
                >
                  <FaDownload style={{ marginRight: 8 }} />
                  Download
                </button>
                <button
                  onClick={() => setSelectedImage(null)}
                  style={{ ...buttonStyle, backgroundColor: '#6B7280', display: 'flex', alignItems: 'center', fontSize: '14px' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={titleStyle}>Gift Card Submissions</h2>
          <span style={badgeStyle}>
            {isMobile ? <FaMobile size={12} /> : <FaDesktop size={12} />}
            {isMobile ? 'Mobile' : 'Desktop'}
          </span>
        </div>
        <button 
          onClick={fetchGiftCards}
          style={buttonStyle}
        >
          Refresh
        </button>
      </div>

      {/* Mobile Card View */}
      {isMobile ? (
        <div>
          {sortedGiftCards.map((card) => (
            <div key={card.id} style={cardStyle}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <h3 style={{ fontWeight: 600, color: '#111827', margin: 0, fontSize: '16px' }}>{card.cardType}</h3>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '4px 0 0 0' }}>
                    {new Date(card.createdAt).toLocaleDateString()} • 
                    {new Date(card.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {/* User Info */}
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#111827', margin: '4px 0' }}>{card.userName}</p>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '2px 0' }}>{card.userEmail}</p>
              </div>

              {/* Image Actions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px 0' }}>Front Image</p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                    <button
                      onClick={() => setSelectedImage(card.frontImageUrl)}
                      style={actionButtonStyle}
                      title="View Front"
                    >
                      <FaEye size={12} />
                    </button>
                    <button
                      onClick={() => downloadImage(card.frontImageUrl, `${card.cardType}-front-${card.id}.jpg`)}
                      style={downloadButtonStyle}
                      title="Download Front"
                    >
                      <FaDownload size={12} />
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '11px', color: '#6B7280', margin: '0 0 4px 0' }}>Back Image</p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                    <button
                      onClick={() => setSelectedImage(card.backImageUrl)}
                      style={actionButtonStyle}
                      title="View Back"
                    >
                      <FaEye size={12} />
                    </button>
                    <button
                      onClick={() => downloadImage(card.backImageUrl, `${card.cardType}-back-${card.id}.jpg`)}
                      style={downloadButtonStyle}
                      title="Download Back"
                    >
                      <FaDownload size={12} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Amount if exists */}
              {card.amount && (
                <div style={{ fontSize: '12px', color: '#6B7280', borderTop: '1px solid #E5E7EB', paddingTop: 8 }}>
                  Amount: ${card.amount}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Desktop Table View */
        <div style={tableContainerStyle}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th 
                    style={thStyle}
                    onClick={() => handleSort("createdAt")}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Date & Time
                      {getSortIcon("createdAt")}
                    </div>
                  </th>
                  <th 
                    style={thStyle}
                    onClick={() => handleSort("cardType")}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      Card Type
                      {getSortIcon("cardType")}
                    </div>
                  </th>
                  <th style={thStyle}>
                    User Information
                  </th>
                  <th style={thStyle}>
                    Images
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedGiftCards.map((card) => (
                  <tr key={card.id} style={{ hover: { backgroundColor: '#F9FAFB' } }}>
                    <td style={tdStyle}>
                      <div style={{ color: '#111827', fontSize: '14px' }}>
                        {new Date(card.createdAt).toLocaleDateString()}
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '14px' }}>
                        {new Date(card.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{card.cardType}</div>
                      {card.amount && (
                        <div style={{ fontSize: '14px', color: '#6B7280' }}>${card.amount}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{card.userName}</div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>{card.userEmail}</div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '12px', color: '#6B7280' }}>Front:</span>
                          <button
                            onClick={() => setSelectedImage(card.frontImageUrl)}
                            style={actionButtonStyle}
                          >
                            <FaEye size={12} />
                            View
                          </button>
                          <button
                            onClick={() => downloadImage(card.frontImageUrl, `${card.cardType}-front-${card.id}.jpg`)}
                            style={downloadButtonStyle}
                          >
                            <FaDownload size={12} />
                            Download
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '12px', color: '#6B7280' }}>Back:</span>
                          <button
                            onClick={() => setSelectedImage(card.backImageUrl)}
                            style={actionButtonStyle}
                          >
                            <FaEye size={12} />
                            View
                          </button>
                          <button
                            onClick={() => downloadImage(card.backImageUrl, `${card.cardType}-back-${card.id}.jpg`)}
                            style={downloadButtonStyle}
                          >
                            <FaDownload size={12} />
                            Download
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {giftCards.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#6B7280' }}>
              No gift card submissions found.
            </div>
          )}
        </div>
      )}

      {/* Footer Info */}
      <div style={{ marginTop: 16, fontSize: '14px', color: '#6B7280', textAlign: 'center' }}>
        Showing {sortedGiftCards.length} gift card submissions • 
        Sorted by {sortField} ({sortDirection})
      </div>
    </div>
  );
};

export default GiftCardAdminView;